import { get, isEmpty, uniqBy, uniq } from 'lodash';
import moment from 'moment';
import { messageFormat } from '@clubmed/utils';
import { ICONS } from 'Components/utils/icon/icons';
import { t } from 'Src/i18n';
import { momentFromApiDate, momentFromApiTime } from 'Utils/date/apiDate';
import { capitalizeName } from 'Utils/label/labelUtils';
type Flight = {
  arrival: {
    date: string;
    id: string;
    time: string;
    label: string;
    airportLabel: string;
  };
  departure: {
    date: string;
    id: string;
    time: string;
    label: string;
    airportLabel: string;
  };
  transport: TransportType;
  duration: string;
  stops: number;
  technicalStops?: number;
  transferIncluded?: boolean;
  stayId?: string;
  travelSections: TravelSections;
  cancellationPolicyType?: string;
  isClubmedTransport: boolean;
  durationInMin?: string;
  travelers?: Array<any>;
  stopDurationInMin?: string;
  connectionDurationInMin?: string;
};
export const BOAT = 'BOAT';
export const PLANE = 'PLANE';
export const TRAIN = 'TRAIN';
export const FLIGHT_CLASS_AVAILABLE_TO_SEARCH = [
  'PLANE_ECONOMY',
  'PLANE_ECONOMY_PLUS',
  'PLANE_BUSINESS',
  'PLANE_PREMIUM',
  'PLANE_FIRST',
];
export const TRAIN_CLASS_AVAILABLE_TO_SEARCH = ['TRAIN_SECOND', 'TRAIN_FIRST'];
export const TRANSPORT_CLASS_TO_I18N = {
  '1CL': 'firstClassLabel',
  '2CL': 'secondClassLabel',
  BUS: 'businessClassLabel',
  DIB: 'emeraldClassLabel',
  DIV: 'economyClassLabel',
  ECO: 'economyClassLabel',
  'EC+': 'alizeeClassLabel',
  FIR: 'firstClassLabel',
  PLANE_BUSINESS: 'businessClassLabel',
  PLANE_ECONOMY: 'economyClassLabel',
  PLANE_ECONOMY_PLUS: 'alizeeClassLabel',
  PLANE_FIRST: 'firstClassLabel',
  PLANE_PREMIUM: 'premiumClassLabel',
  PRC: 'premiumClassLabel',
  PRE: 'premiumClassLabel',
  TRAIN_FIRST: 'firstClassLabel',
  TRAIN_SECOND: 'secondClassLabel',
};
export const SNCF_ID = 'SNC';

function getDiffAbsoluteDays(fromDate: string, toDate: string): number | null {
  const fromMoment = momentFromApiDate(fromDate);
  const toMoment = momentFromApiDate(toDate);

  if (fromMoment.isValid() && toMoment.isValid()) {
    return toMoment.diff(fromMoment, 'days');
  }

  return null;
}

function formatTime(time: string, format: string): string | null {
  const renderedTime: string = momentFromApiTime(time).format(format);
  return renderedTime !== 'Invalid date' ? renderedTime : null;
}

function formatDate(date: string, format?: string): string {
  return capitalizeName(
    momentFromApiDate(date).format(format || t('common.verboseDateFormatTechnicalID')),
  );
}

function getWayLabel(i18n: CMSContent, way: string) {
  if (way === 'INBOUND') {
    return i18n?.common?.transport?.inboundLabel;
  }

  if (way === 'OUTBOUND') {
    return i18n?.common?.transport?.outboundLabel;
  }

  return null;
}

function formatConnectionLabel(
  label: string | null | undefined,
  duration: string | null | undefined,
): string {
  const durationAsNumber = duration ? +duration : 0;
  const durationFormated = moment.utc(
    moment.duration(durationAsNumber, 'minutes').asMilliseconds(),
  );
  return messageFormat('{label} {duration}').format({
    label,
    duration: `${durationFormated.format('HH')}h${durationFormated.format('mm')}`,
  });
}

function getStep(i18n: CMSContent): (arg0: {}) => {} {
  const { timeFormat } = i18n?.common || {};
  const transportConnectionLabel = i18n?.common?.transport?.connection;
  return (step) => {
    const connectionDuration = get(step, 'connectionDurationInMin');
    const stopOverDuration = get(step, 'stopDurationInMin');
    const information =
      !isEmpty(stopOverDuration) || !isEmpty(connectionDuration)
        ? formatConnectionLabel(transportConnectionLabel, stopOverDuration || connectionDuration)
        : null;
    return {
      id: get(step, 'transport.reference'),
      type: get(step, 'transport.type'),
      information,
      operator: get(step, 'transport.companyOperator.label'),
      operatorId: get(step, 'transport.companyOperator.id'),
      operatorLogo: get(step, 'transport.companyOperator.logo'),
      start: {
        label: get(step, 'departure.airportLabel'),
        time: formatTime(get(step, 'departure.time'), timeFormat),
        date: get(step, 'departure.date'),
      },
      end: {
        label: get(step, 'arrival.airportLabel'),
        time: formatTime(get(step, 'arrival.time'), timeFormat),
        date: get(step, 'arrival.date'),
      },
      technicalStops: get(step, 'technicalStops'),
    };
  };
}

function hasOnlyBoat(travelSections: TravelSections | null | undefined) {
  if (!travelSections || !travelSections.length) {
    return false;
  }

  return travelSections.every((section) => get(section, 'transport.type') === BOAT);
}

function hasBoat(travelSections: TravelSections | null | undefined) {
  if (!travelSections || !travelSections.length) {
    return false;
  }

  return travelSections.some((section) => get(section, 'transport.type') === BOAT);
}

function hasPlane(travelSections: TravelSections | null | undefined) {
  if (!travelSections || !travelSections.length) {
    return false;
  }

  return travelSections.some((section) => get(section, 'transport.type') === PLANE);
}

function hasTrain(travelSections: TravelSections | null | undefined) {
  if (!travelSections || !travelSections.length) {
    return false;
  }

  return travelSections.some((section) => get(section, 'transport.type') === TRAIN);
}

function hasOnlyTrain(travelSections: TravelSections | null | undefined) {
  if (!travelSections || !travelSections.length) {
    return false;
  }

  return travelSections.every((section) => get(section, 'transport.type') === TRAIN);
}

function hasOnlyPlanes(travelSections: TravelSections | null | undefined = undefined) {
  return !(hasTrain(travelSections) || hasBoat(travelSections));
}

function isTransportTrain(
  inbound: TransportDetail | null | undefined,
  outbound: TransportDetail | null | undefined,
) {
  return (
    hasOnlyTrain(get(inbound, 'travelSections')) && hasOnlyTrain(get(outbound, 'travelSections'))
  );
}

function isTransportPlaneTicket(
  inbound: TransportDetail | null | undefined = undefined,
  outbound: TransportDetail | null | undefined = undefined,
) {
  return (
    hasOnlyPlanes(get(inbound, 'travelSections')) && hasOnlyPlanes(get(outbound, 'travelSections'))
  );
}

function getTransportIcon(
  inbound: (TransportDetail | null | undefined) | (Flight | null | undefined) = undefined,
  outbound: (TransportDetail | null | undefined) | (Flight | null | undefined) = undefined,
): string {
  const isTrain =
    hasTrain(get(inbound, 'travelSections')) && hasTrain(get(outbound, 'travelSections'));

  if (isTrain) {
    return ICONS.TRAIN;
  }

  const isBoat =
    hasOnlyBoat(get(inbound, 'travelSections')) && hasOnlyBoat(get(outbound, 'travelSections'));

  if (isBoat) {
    return ICONS.BOAT;
  }

  return ICONS.TRANSPORT;
}

function tripHasSchedules(travelSections: TravelSections | null | undefined): boolean {
  return !isEmpty(travelSections)
    ? (travelSections || []).some(
        (section) => get(section, 'arrival.time') || get(section, 'departure.time'),
      )
    : false;
}

function tripsHaveSchedules(
  inbound: TransportDetail | null | undefined,
  outbound: TransportDetail | null | undefined,
): boolean {
  return (
    tripHasSchedules(get(inbound, 'travelSections')) ||
    tripHasSchedules(get(outbound, 'travelSections'))
  );
}

function transformMinutesInHours(minutes: string | number, isRTL: boolean = false): string {
  const numberMinutes = Number(minutes);

  if (numberMinutes < 60) {
    return `${numberMinutes}${t('options.countdownLabels.min')}`;
  }

  if (numberMinutes % 60 === 0) {
    return `${numberMinutes / 60}${t('options.countdownLabels.hours')}`;
  }

  const time = [
    Math.floor(numberMinutes / 60),
    t('options.countdownLabels.hours'),
    `${isRTL ? '-' : ''}`,
    (numberMinutes % 60).toString().padStart(2, '0'),
    t('options.countdownLabels.min'),
  ];
  return time.join('');
}

function getCompanyOperators(travelSections: Array<TravelSection> = []): Array<TransportCompany> {
  const companyOperators = travelSections.reduce((companies, section: TravelSection) => {
    if (section?.transport?.companyOperator?.id && section?.transport?.companyOperator?.label) {
      companies.push(section.transport.companyOperator);
    }

    return companies;
  }, []);
  return uniqBy(companyOperators, 'id');
}

function getCompanyOperatorsName(travelSections: Array<TravelSection> = []): Array<string> {
  const companyOperators = getCompanyOperators(travelSections);
  return uniqBy(companyOperators, 'id').map((company) => capitalizeName(company.label));
}

function getTransportClasses(travelSections: Array<TravelSection> = []): Array<string> {
  const allClasses = travelSections
    .map((section: TravelSection) => section?.transport?.class)
    .filter(Boolean);
  return uniq(allClasses);
}

export const getFindSectionTransportTypeFn =
  (transportType: string) =>
  ({ transport: { type } }: TravelSection) =>
    type === transportType;
type props = {
  transportType?: string;
  travelSections: Array<any>;
  inboundCriteria?: any;
};
export const getClassLabel = ({
  transportType,
  travelSections,
  inboundCriteria,
}: props): string | null | undefined => {
  const transportClass =
    inboundCriteria?.flightClass ||
    get(
      transportType
        ? travelSections.find(getFindSectionTransportTypeFn(transportType))
        : travelSections[0],
      'transport.class',
    );

  if (!TRANSPORT_CLASS_TO_I18N[transportClass]) {
    return null;
  }

  return t(`common.transport.transportClass.${TRANSPORT_CLASS_TO_I18N[transportClass]}`);
};
export {
  getDiffAbsoluteDays,
  formatTime,
  formatDate,
  getWayLabel,
  formatConnectionLabel,
  getStep,
  hasPlane,
  hasOnlyPlanes,
  hasOnlyTrain,
  isTransportTrain,
  isTransportPlaneTicket,
  tripsHaveSchedules,
  transformMinutesInHours,
  getTransportIcon,
  getCompanyOperators,
  getCompanyOperatorsName,
  getTransportClasses,
};
